import React, { useCallback, useLayoutEffect, useMemo, useRef } from 'react'
import { Music } from 'lucide-react'
import WordLevelLine from './WordLevelLine'

const BRACKET_MARKER_REGEX = /^\s*\[[^\]]+\]\s*$/

const toFiniteNumber = (value) => {
  const numeric = Number(value)
  return Number.isFinite(numeric) ? numeric : null
}

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))
const DEFAULT_COUNT_IN_MAX_DURATION_SECONDS = 5
const DEFAULT_COUNT_IN_MIN_DURATION_SECONDS = 2
const COUNT_IN_FADE_IN_SECONDS = 1
const COUNT_IN_FADE_OUT_SECONDS = 0.7

const isNonVocalMarkerLine = (text = '') => {
  const normalized = text.trim()
  if (!normalized) return false
  if (normalized === '...') return true
  return BRACKET_MARKER_REGEX.test(normalized)
}

const escapeHtml = (value = '') =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')

const clearPlaceholder = (element) => {
  if (!element) return
  if (element.dataset.placeholderActive === 'true') {
    element.textContent = ''
    element.dataset.placeholderActive = 'false'
  }
}

const showPlaceholder = (element, placeholder) => {
  if (!element) return
  element.textContent = placeholder
  element.dataset.placeholderActive = 'true'
}

const getCurrentUpcomingCountInKey = (candidates, currentTime) => {
  if (!Array.isArray(candidates) || candidates.length === 0) {
    return null
  }

  for (const candidate of candidates) {
    if (
      !candidate ||
      !Number.isFinite(candidate.startTime) ||
      !Number.isFinite(candidate.endTime) ||
      candidate.endTime <= candidate.startTime
    ) {
      continue
    }

    const fadeInStartTime = candidate.startTime - COUNT_IN_FADE_IN_SECONDS
    if (
      currentTime >= fadeInStartTime &&
      currentTime < (candidate.endTime + COUNT_IN_FADE_OUT_SECONDS)
    ) {
      return candidate.key
    }
  }

  return null
}

const LyricsDisplay = ({
  lyrics,
  currentLyricIndex,
  audioError,
  audioLoading,
  onLyricClick,
  lyricsContainerRef,
  formatTime,
  showLyrics = true,
  showTranslation = true,
  showRomanization = true,
  isStudioMode = false,
  onLyricChange,
  onCommitLyric,
  placeholderTexts = {},
  // Word-level karaoke props
  wordLevelTranscription = null,
  currentTime = 0,
  isPlaying = false,
  forceLineMode = false,
  autoScrollEnabled = true,
  enableCountIn = false,
  countInGapThresholdSeconds = 5,
  countInMaxDurationSeconds = DEFAULT_COUNT_IN_MAX_DURATION_SECONDS,
  countInMinDurationSeconds = DEFAULT_COUNT_IN_MIN_DURATION_SECONDS
}) => {
  // Determine if we should use word-level display
  const useWordLevel = useMemo(() => {
    return !forceLineMode &&
      !isStudioMode && // Don't use word-level in studio mode (editing requires line-level)
      wordLevelTranscription &&
      wordLevelTranscription.length > 0
  }, [forceLineMode, isStudioMode, wordLevelTranscription])

  const activeFieldRef = useRef({ index: null, field: null })

  // Turning auto-scroll off must also cancel a smooth scroll that is already
  // running. Disabling scroll anchoring prevents the browser from moving the
  // viewport as the active lyric's DOM state changes.
  useLayoutEffect(() => {
    if (autoScrollEnabled) return

    const container = lyricsContainerRef?.current
    if (!container) return

    const currentScrollTop = container.scrollTop
    if (typeof container.scrollTo === 'function') {
      container.scrollTo({
        top: currentScrollTop,
        behavior: 'auto'
      })
    } else {
      container.scrollTop = currentScrollTop
    }
  }, [autoScrollEnabled, lyricsContainerRef])

  const lyricScrollStyle = autoScrollEnabled
    ? undefined
    : { overflowAnchor: 'none', scrollBehavior: 'auto' }

  const handleInput = useCallback((index, field, value) => {
    if (!onLyricChange) return
    onLyricChange(index, field, value)
  }, [onLyricChange])

  const handleKeyDown = useCallback((event, index, field) => {
    if (!onCommitLyric) return

    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      onCommitLyric(index, field)
    } else if (event.key === 'Escape') {
      event.preventDefault()
      event.currentTarget.blur()
    }
  }, [onCommitLyric])

  const handleFocus = useCallback((event, index, field) => {
    activeFieldRef.current = { index, field }
    const element = event.currentTarget
    requestAnimationFrame(() => {
      if (document.activeElement === element) {
        const selection = window.getSelection()
        if (!selection) return
        const range = document.createRange()
        range.selectNodeContents(element)
        range.collapse(false)
        selection.removeAllRanges()
        selection.addRange(range)
      }
    })
  }, [])

  const getDisplayText = useCallback((lyric, field) => {
    const text = lyric?.[field]
    if (!text || text.trim().length === 0) {
      return ''
    }
    return text
  }, [])

  const getPlaceholder = useCallback((field) => {
    if (placeholderTexts && placeholderTexts[field]) {
      return placeholderTexts[field]
    }
    switch (field) {
      case 'translation':
        return 'Add translation...'
      case 'romanization':
        return 'Add romanization...'
      default:
        return 'Add lyrics...'
    }
  }, [placeholderTexts])

  const renderTextField = useCallback(({ index, field, isPrimary, lyric, isCurrent, isPast }) => {
    const text = getDisplayText(lyric, field)
    const placeholder = getPlaceholder(field)
    const hasText = text && text.trim().length > 0
    const colorClass = isPrimary
      ? (isCurrent ? 'text-white' : isPast ? 'text-white/25 hover:text-white/80' : 'text-white/50 hover:text-white')
      : (isCurrent ? 'text-white/90' : isPast ? 'text-white/20 hover:text-white/70' : 'text-white/40 hover:text-white/80')

    if (!isStudioMode) {
      if (!hasText) return null

      return (
        <div
          className={`font-bold lyrics-text ${colorClass} ${isPrimary ? '' : 'text-xl'}`}
          style={{ fontSize: isPrimary ? '40px' : undefined, lineHeight: 1.3 }}
        >
          {text}
        </div>
      )
    }

    const initialContent = hasText ? escapeHtml(text) : ''

    return (
      <div
        key={`${index}-${field}`}
        className={`font-bold lyrics-text editable-lyric-field ${colorClass} ${isPrimary ? '' : 'text-xl'} ${hasText ? '' : 'italic text-white/40'}`}
        style={{ fontSize: isPrimary ? '40px' : undefined, lineHeight: 1.3 }}
        contentEditable
        suppressContentEditableWarning
        data-lyric-field={field}
        data-lyric-index={index}
        data-placeholder={placeholder}
        data-placeholder-active={hasText ? 'false' : 'true'}
        onInput={(event) => {
          const value = event.currentTarget.textContent || ''
          if (value.trim().length > 0) {
            event.currentTarget.dataset.placeholderActive = 'false'
            event.currentTarget.classList.remove('italic', 'text-white/40')
          } else {
            event.currentTarget.dataset.placeholderActive = 'true'
            event.currentTarget.classList.add('italic', 'text-white/40')
          }
          handleInput(index, field, value)
        }}
        onBlur={(event) => {
          const value = event.currentTarget.textContent || ''
          if (onCommitLyric) {
            onCommitLyric(index, field)
          }
          if (value.trim().length === 0) {
            showPlaceholder(event.currentTarget, placeholder)
            event.currentTarget.classList.add('italic', 'text-white/40')
          }
          event.currentTarget.classList.remove('editing')
        }}
        onKeyDown={(event) => handleKeyDown(event, index, field)}
        onFocus={(event) => {
          event.currentTarget.classList.add('editing')
          clearPlaceholder(event.currentTarget)
          handleFocus(event, index, field)
        }}
        onMouseDown={(event) => {
          if (event.currentTarget.dataset.placeholderActive === 'true') {
            event.currentTarget.dataset.internalSelectAll = 'true'
          }
        }}
        onMouseUp={(event) => {
          if (event.currentTarget.dataset.internalSelectAll === 'true') {
            event.currentTarget.dataset.internalSelectAll = 'false'
            const selection = window.getSelection()
            if (!selection) return
            const range = document.createRange()
            range.selectNodeContents(event.currentTarget)
            selection.removeAllRanges()
            selection.addRange(range)
          }
        }}
        dangerouslySetInnerHTML={{ __html: initialContent }}
      />
    )
  }, [getDisplayText, getPlaceholder, handleFocus, handleInput, handleKeyDown, isStudioMode, onCommitLyric])

  const instrumentPlaceholder = useMemo(() => placeholderTexts?.instrumental || 'Instrumental break', [placeholderTexts])

  const getCountInProgress = useCallback((startTime, endTime) => {
    if (!Number.isFinite(startTime) || !Number.isFinite(endTime) || endTime <= startTime) {
      return 0
    }
    return clamp(((currentTime - startTime) / (endTime - startTime)) * 100, 0, 100)
  }, [currentTime])

  const getCountInOpacity = useCallback((startTime, endTime) => {
    if (!Number.isFinite(startTime) || !Number.isFinite(endTime) || endTime <= startTime) {
      return 0
    }

    const fadeInStart = startTime - COUNT_IN_FADE_IN_SECONDS
    if (currentTime < fadeInStart) return 0

    if (currentTime < startTime) {
      return clamp((currentTime - fadeInStart) / COUNT_IN_FADE_IN_SECONDS, 0, 1)
    }

    if (currentTime <= endTime) return 1

    if (currentTime < (endTime + COUNT_IN_FADE_OUT_SECONDS)) {
      return clamp(1 - ((currentTime - endTime) / COUNT_IN_FADE_OUT_SECONDS), 0, 1)
    }

    return 0
  }, [currentTime])

  const getEffectiveCountInStart = useCallback((startTime, endTime) => {
    if (!Number.isFinite(endTime)) return null
    const latestStart = endTime - countInMaxDurationSeconds
    if (Number.isFinite(startTime)) {
      return Math.max(startTime, latestStart)
    }
    return latestStart
  }, [countInMaxDurationSeconds])

  const renderCountInBar = useCallback(({ startTime, endTime, isCurrent }) => {
    const progress = getCountInProgress(startTime, endTime)
    const opacity = getCountInOpacity(startTime, endTime)

    return (
      <div className="mx-auto w-full max-w-sm px-4 py-2">
        <div className="relative w-full h-2">
          <div
            className={`absolute inset-0 rounded-full transition-opacity duration-100 ease-linear ${isCurrent ? 'bg-white/30' : 'bg-white/20'}`}
            style={{ opacity }}
          >
            <div
              className="bg-white rounded-full h-2 transition-all duration-200 pointer-events-none"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>
    )
  }, [getCountInOpacity, getCountInProgress])

  const renderCountInSpacer = useCallback(() => (
    <div className="mx-auto w-full max-w-sm px-4 py-2">
      <div className="w-full h-2" />
    </div>
  ), [])

  const shouldRenderSection = (field, lyric) => {
    if (field === 'text') return showLyrics
    if (field === 'romanization') {
      return showRomanization && lyric.romanization && lyric.romanization.trim().length > 0
    }
    if (field === 'translation') {
      return showTranslation && lyric.translation && lyric.translation.trim().length > 0
    }
    return false
  }

  if (audioError) {
    return (
      <div className="text-center text-white/60">
        <Music className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p className="text-lg font-medium">Audio Error</p>
        <p className="text-sm opacity-75 mt-2">
          {audioError}
        </p>
      </div>
    )
  }

  if (audioLoading) {
    return (
      <div className="text-center text-white/60">
        <Music className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p className="text-lg font-medium">Loading audio...</p>
      </div>
    )
  }

  if (!lyrics || lyrics.length === 0) {
    return (
      <div className="text-center text-white/60">
        <Music className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p className="text-lg font-medium">No lyrics available</p>
      </div>
    )
  }

  // Word-level karaoke display
  if (useWordLevel) {
    const wordLevelCurrentIndex = currentLyricIndex
    const firstWordLineWithSegments = wordLevelTranscription.find(
      (line) => line?.segments && line.segments.length > 0
    )
    const firstWordLevelStart = toFiniteNumber(firstWordLineWithSegments?.segments?.[0]?.start_time)
    const firstWordLevelCountInEnd = Number.isFinite(firstWordLevelStart)
      ? firstWordLevelStart
      : null
    const firstWordLevelCountInStart = getEffectiveCountInStart(0, firstWordLevelCountInEnd)
    const wordLevelStartCountInCandidate = (
      enableCountIn &&
      Number.isFinite(firstWordLevelCountInStart) &&
      Number.isFinite(firstWordLevelCountInEnd) &&
      (firstWordLevelCountInEnd - firstWordLevelCountInStart) >= countInMinDurationSeconds
    )
      ? {
        key: 'start-count-in-word-level',
        startTime: firstWordLevelCountInStart,
        endTime: firstWordLevelCountInEnd
      }
      : null

    const wordLevelGapCountInCandidates = enableCountIn
      ? wordLevelTranscription.map((line, index) => {
        const prevLine = index > 0 ? wordLevelTranscription[index - 1] : null
        const nextLine = index < wordLevelTranscription.length - 1 ? wordLevelTranscription[index + 1] : null
        const firstSegment = line?.segments?.[0]
        const lastSegment = line?.segments?.[line.segments?.length - 1]
        const prevLastSegment = prevLine?.segments?.[prevLine?.segments?.length - 1]
        const nextFirstSegment = nextLine?.segments?.[0]

        const lineStart = toFiniteNumber(firstSegment?.start_time)
        const lineEnd = toFiniteNumber(lastSegment?.end_time)
        const prevLineEnd = toFiniteNumber(prevLastSegment?.end_time)
        const nextLineStart = toFiniteNumber(nextFirstSegment?.start_time)
        const hasUpcomingLine = Number.isFinite(nextLineStart)
        const isInstrumentalLine = !line.segments || line.segments.length === 0
        const hasLongGapToNext = (
          Number.isFinite(lineEnd) &&
          Number.isFinite(nextLineStart) &&
          (nextLineStart - lineEnd) > countInGapThresholdSeconds
        )

        const shouldShowGapCountIn = (
          hasUpcomingLine &&
          (isInstrumentalLine || hasLongGapToNext)
        )
        if (!shouldShowGapCountIn) return null

        const rawCountInStart = isInstrumentalLine ? (lineStart ?? prevLineEnd) : lineEnd
        const gapCountInEnd = nextLineStart
        const gapCountInStart = getEffectiveCountInStart(rawCountInStart, gapCountInEnd)
        const isValidGapCountIn = (
          Number.isFinite(gapCountInStart) &&
          Number.isFinite(gapCountInEnd) &&
          (gapCountInEnd - gapCountInStart) >= countInMinDurationSeconds
        )
        if (!isValidGapCountIn) return null

        return {
          key: `word-gap-${index}`,
          startTime: gapCountInStart,
          endTime: gapCountInEnd
        }
      }).filter(Boolean)
      : []

    const wordLevelCountInCandidates = [
      ...(wordLevelStartCountInCandidate ? [wordLevelStartCountInCandidate] : []),
      ...wordLevelGapCountInCandidates
    ].sort((a, b) => a.startTime - b.startTime)
    const visibleWordLevelCountInKey = getCurrentUpcomingCountInKey(wordLevelCountInCandidates, currentTime)
    const wordLevelCountInCandidateMap = new Map(
      wordLevelCountInCandidates.map((candidate) => [candidate.key, candidate])
    )

    return (
      <div
        ref={lyricsContainerRef}
        className="lyrics-container lyrics-scroll-container scrollbar-hidden w-full h-full mx-auto overflow-y-auto"
        style={lyricScrollStyle}
      >
        <div className="space-y-6 py-8 px-4" style={{ paddingTop: '150px', paddingBottom: '100vh' }}>
          {wordLevelStartCountInCandidate ? (
            <div
              key="start-count-in-word-level"
              data-lyric-index="-1"
              className="text-center transition-all duration-500 cursor-pointer px-4 py-2"
            >
              {visibleWordLevelCountInKey === 'start-count-in-word-level'
                ? renderCountInBar({
                  startTime: firstWordLevelCountInStart,
                  endTime: firstWordLevelCountInEnd,
                  isCurrent: true
                })
                : renderCountInSpacer()}
            </div>
          ) : null}
          {wordLevelTranscription.map((line, index) => {
            const isCurrent = index === wordLevelCurrentIndex
            const isPast = index < wordLevelCurrentIndex
            const isInstrumentalLine = !line.segments || line.segments.length === 0
            const gapCandidateKey = `word-gap-${index}`
            const gapCandidate = wordLevelCountInCandidateMap.get(gapCandidateKey)
            const shouldRenderGapCountInBar = (
              visibleWordLevelCountInKey === gapCandidateKey &&
              !!gapCandidate
            )

            const lineNode = isInstrumentalLine ? (
              <div
                data-lyric-index={index}
                data-scroll-anchor={enableCountIn ? 'false' : 'true'}
                className="text-center transition-all duration-500 cursor-pointer px-4 py-2"
              >
                {enableCountIn ? (
                  <div className="h-6" />
                ) : (
                  <div className={`font-bold text-4xl ${isCurrent ? 'text-white' : isPast ? 'text-white/40' : 'text-white/70'}`}>
                    ...
                  </div>
                )}
              </div>
            ) : (
              <div data-lyric-index={index} data-scroll-anchor="true">
                <WordLevelLine
                  segments={line.segments}
                  translation={line.translation}
                  romanization={line.romanization}
                  currentTime={currentTime}
                  isPlaying={isPlaying}
                  isCurrent={isCurrent}
                  isPast={isPast}
                  showLyrics={showLyrics}
                  showTranslation={showTranslation}
                  showRomanization={showRomanization}
                  onClick={(seekTime) => onLyricClick && onLyricClick({ start_time: seekTime }, index)}
                />
              </div>
            )

            return (
              <React.Fragment key={`word-level-${index}`}>
                {lineNode}
                {gapCandidate ? (
                  <div className="text-center transition-all duration-500 cursor-pointer px-4 py-2">
                    {shouldRenderGapCountInBar
                      ? renderCountInBar({
                        startTime: gapCandidate.startTime,
                        endTime: gapCandidate.endTime,
                        isCurrent
                      })
                      : renderCountInSpacer()}
                  </div>
                ) : null}
              </React.Fragment>
            )
          })}
        </div>
      </div>
    )
  }

  // Line-level display (original behavior)
  const firstLyricStart = toFiniteNumber(lyrics[0]?.start_time)
  const firstLyricCountInEnd = Number.isFinite(firstLyricStart)
    ? firstLyricStart
    : null
  const firstLyricCountInStart = getEffectiveCountInStart(0, firstLyricCountInEnd)
  const lineLevelStartCountInCandidate = (
    enableCountIn &&
    !isStudioMode &&
    Number.isFinite(firstLyricCountInStart) &&
    Number.isFinite(firstLyricCountInEnd) &&
    (firstLyricCountInEnd - firstLyricCountInStart) >= countInMinDurationSeconds
  )
    ? {
      key: 'start-count-in-line-level',
      startTime: firstLyricCountInStart,
      endTime: firstLyricCountInEnd
    }
    : null

  const lineLevelGapCountInCandidates = enableCountIn && !isStudioMode
    ? lyrics.map((lyric, index) => {
      const prevLyric = index > 0 ? lyrics[index - 1] : null
      const nextLyric = index < lyrics.length - 1 ? lyrics[index + 1] : null
      const lyricStartTime = toFiniteNumber(lyric?.start_time)
      const lyricEndTime = toFiniteNumber(lyric?.end_time)
      const prevLyricEndTime = toFiniteNumber(prevLyric?.end_time)
      const nextLyricStartTime = toFiniteNumber(nextLyric?.start_time)
      const isBracketMarker = isNonVocalMarkerLine(lyric?.text || '')
      const hasUpcomingLyric = Number.isFinite(nextLyricStartTime)
      const hasLongGapToNext = (
        Number.isFinite(lyricEndTime) &&
        Number.isFinite(nextLyricStartTime) &&
        (nextLyricStartTime - lyricEndTime) > countInGapThresholdSeconds
      )

      const shouldShowGapCountIn = (
        hasUpcomingLyric &&
        (isBracketMarker || hasLongGapToNext)
      )
      if (!shouldShowGapCountIn) return null

      const rawCountInStart = isBracketMarker
        ? (lyricStartTime ?? prevLyricEndTime)
        : lyricEndTime
      const countInEnd = nextLyricStartTime
      const countInStart = getEffectiveCountInStart(rawCountInStart, countInEnd)
      const isValidGapCountIn = (
        Number.isFinite(countInStart) &&
        Number.isFinite(countInEnd) &&
        (countInEnd - countInStart) >= countInMinDurationSeconds
      )
      if (!isValidGapCountIn) return null

      return {
        key: `line-gap-${index}`,
        startTime: countInStart,
        endTime: countInEnd
      }
    }).filter(Boolean)
    : []

  const lineLevelCountInCandidates = [
    ...(lineLevelStartCountInCandidate ? [lineLevelStartCountInCandidate] : []),
    ...lineLevelGapCountInCandidates
  ].sort((a, b) => a.startTime - b.startTime)
  const visibleLineLevelCountInKey = getCurrentUpcomingCountInKey(lineLevelCountInCandidates, currentTime)
  const lineLevelCountInCandidateMap = new Map(
    lineLevelCountInCandidates.map((candidate) => [candidate.key, candidate])
  )

  return (
    <div
      ref={lyricsContainerRef}
      className="lyrics-container lyrics-scroll-container scrollbar-hidden w-full h-full mx-auto overflow-y-auto"
      style={lyricScrollStyle}
    >
        <div className="space-y-6 py-8 px-4" style={{ paddingTop: '150px', paddingBottom: '100vh' }}>
          {lineLevelStartCountInCandidate ? (
            <div
              key="start-count-in-line-level"
              data-lyric-index="-1"
              className="text-center transition-all duration-500 cursor-pointer px-4 py-2"
            >
              {visibleLineLevelCountInKey === 'start-count-in-line-level'
                ? renderCountInBar({
                  startTime: firstLyricCountInStart,
                  endTime: firstLyricCountInEnd,
                  isCurrent: true
                })
                : renderCountInSpacer()}
            </div>
          ) : null}
          {lyrics.map((lyric, index) => {
            const isCurrent = index === currentLyricIndex
            const isPast = index < currentLyricIndex
            const isBracketMarker = isNonVocalMarkerLine(lyric?.text || '')
            const shouldSuppressBracketMarker = enableCountIn && !isStudioMode && isBracketMarker
            const gapCandidateKey = `line-gap-${index}`
            const gapCandidate = lineLevelCountInCandidateMap.get(gapCandidateKey)
            const shouldRenderGapCountInBar = (
              visibleLineLevelCountInKey === gapCandidateKey &&
              !!gapCandidate
            )

            return (
              <React.Fragment key={`line-level-${index}`}>
                <div
                  data-lyric-index={index}
                  data-scroll-anchor={shouldSuppressBracketMarker ? 'false' : 'true'}
                  onClick={() => onLyricClick && onLyricClick(lyric, index)}
                  className={`
                    text-center transition-all duration-500 cursor-pointer px-4 py-2
                    ${isStudioMode ? 'hover:bg-white/10 rounded-lg' : ''}
                    ${isStudioMode && isCurrent ? 'bg-white/10' : ''}
                  `}
                  style={{ textShadow: isCurrent ? '0 0 20px rgba(255,255,255,0.3)' : 'none' }}
                >
                  {(() => {

                    if (shouldSuppressBracketMarker) {
                      return <div className="h-6" />
                    }

                    const isInstrumental = lyric.text && lyric.text.trim() === '...'

                    if (isInstrumental) {
                      const colorClass = isCurrent
                        ? 'text-white'
                        : isPast
                          ? 'text-white/50 hover:text-white/80'
                          : 'text-white/70 hover:text-white'

                      if (!isStudioMode) {
                        return (
                          <div
                            className={`font-bold ${colorClass} lyrics-text text-4xl`}
                            style={{ lineHeight: 1.2 }}
                          >
                            ...
                          </div>
                        )
                      }

                      return (
                        <div
                          className={`font-bold lyrics-text editable-lyric-field ${colorClass} text-4xl ${isStudioMode ? 'hover:text-white' : ''}`}
                          style={{ lineHeight: 1.2 }}
                          contentEditable
                          suppressContentEditableWarning
                          data-lyric-field="text"
                          data-lyric-index={index}
                          data-placeholder={instrumentPlaceholder}
                          data-placeholder-active={lyric.text && lyric.text.trim() !== '...' ? 'false' : 'true'}
                          onInput={(event) => {
                            const value = event.currentTarget.textContent || ''
                            if (value.trim().length > 0) {
                              event.currentTarget.dataset.placeholderActive = 'false'
                              event.currentTarget.classList.remove('italic', 'text-white/40')
                            } else {
                              event.currentTarget.dataset.placeholderActive = 'true'
                              event.currentTarget.classList.add('italic', 'text-white/40')
                            }
                            handleInput(index, 'text', value)
                          }}
                          onBlur={(event) => {
                            const value = event.currentTarget.textContent || ''
                            if (onCommitLyric) {
                              onCommitLyric(index, 'text')
                            }
                            if (value.trim().length === 0) {
                              showPlaceholder(event.currentTarget, instrumentPlaceholder)
                              event.currentTarget.classList.add('italic', 'text-white/40')
                            }
                          }}
                          onKeyDown={(event) => handleKeyDown(event, index, 'text')}
                          onFocus={(event) => {
                            clearPlaceholder(event.currentTarget)
                            handleFocus(event, index, 'text')
                          }}
                          onMouseDown={(event) => {
                            if (event.currentTarget.dataset.placeholderActive === 'true') {
                              event.currentTarget.dataset.internalSelectAll = 'true'
                            }
                          }}
                          onMouseUp={(event) => {
                            if (event.currentTarget.dataset.internalSelectAll === 'true') {
                              event.currentTarget.dataset.internalSelectAll = 'false'
                              const selection = window.getSelection()
                              if (!selection) return
                              const range = document.createRange()
                              range.selectNodeContents(event.currentTarget)
                              selection.removeAllRanges()
                              selection.addRange(range)
                            }
                          }}
                          dangerouslySetInnerHTML={{ __html: lyric.text && lyric.text.trim() !== '...' ? escapeHtml(lyric.text) : '' }}
                        />
                      )
                    }

                    const sections = []

                    if (shouldRenderSection('text', lyric)) {
                      sections.push(renderTextField({ index, field: 'text', isPrimary: true, lyric, isCurrent, isPast }))
                    }
                    if (shouldRenderSection('romanization', lyric)) {
                      sections.push(renderTextField({ index, field: 'romanization', isPrimary: !showLyrics, lyric, isCurrent, isPast }))
                    }
                    if (shouldRenderSection('translation', lyric)) {
                      sections.push(renderTextField({ index, field: 'translation', isPrimary: !showLyrics && !showRomanization, lyric, isCurrent, isPast }))
                    }

                    if (sections.length === 0) {
                      return (
                        <div className="text-sm text-white/40 italic">
                          {placeholderTexts?.emptyLine || 'No lyric data'}
                        </div>
                      )
                    }

                    return (
                      <div className="space-y-2">
                        {sections.map((section, sectionIndex) => (
                          <React.Fragment key={sectionIndex}>
                            {section}
                          </React.Fragment>
                        ))}
                      </div>
                    )
                  })()}
                </div>
                {gapCandidate ? (
                  <div className="text-center transition-all duration-500 cursor-pointer px-4 py-2">
                    {shouldRenderGapCountInBar
                      ? renderCountInBar({
                        startTime: gapCandidate.startTime,
                        endTime: gapCandidate.endTime,
                        isCurrent
                      })
                      : renderCountInSpacer()}
                  </div>
                ) : null}
              </React.Fragment>
            )
          })}
      </div>
    </div>
  )
}

export default LyricsDisplay
